<!--
// app/pages/teams/[team].vue
-->
<script setup lang="ts">
import type { Team, TeamRankingParameter } from "~/src/team/domain/models/Team";
import type { Staff } from "~/src/team/domain/models/Staff";
import type { Standing } from "~/src/standing/domain/models/Standing";
import type { SocialMediaNames } from "~/src/region/domain/models/Region";
import type { Player } from "~/src/player/domain/Player";
import type { UnwrapRefSimple } from "@vue/reactivity";
import type { Season } from "~/src/season-cluster/domain/models/01.Season";
import { useTeams } from "~/app/composables/teams";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useTeamAndPlayerStore } from "~/app/stores/teamAndPlayerStore";
import { useWebContext } from "~/app/composables/webContext";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";
import { useRegions } from "~/app/composables/regions";
import { slugify } from "~/src/Shared/utils";
import RegionUiService from "~/src/region/infrastructure/ui-services/regionUiService";
import CurrentSeasonSliderMatchesTeam from "~/app/components/Shared/slider-matches/CurrentSeasonSliderMatchesTeam.vue";
import CardStoreAd from "~/app/components/Shared/CardStoreAd.vue";
import CardPresidentTeamPage from "~/app/components/pages/teams-team/CardPresidentTeamPage.vue";
import CardCoachTeamPage from "~/app/components/pages/teams-team/CardCoachTeamPage.vue";
import GridHistoricDataTeamPage from "~/app/components/pages/teams-team/GridHistoricDataTeamPage.vue";
import CurrentSeasonTableDataTeamPage from "~/app/components/pages/teams-team/CurrentSeasonTableDataTeamPage.vue";
import BaseBackdropLoading from "~/app/components/base/BaseBackdropLoading.vue";
import CardNoDataMessage from "~/app/components/Shared/CardNoDataMessage.vue";
import TeamSeasonsFilter from "~/app/components/pages/teams-team/TeamSeasonsFilter.vue";
import HeroTeamPage from "~/app/components/pages/teams-team/HeroTeamPage.vue";
import {
  $SEASONS_WORLD_CUP,
  $SEASONS_NATIONS_GROUPS,
} from "~/src/season-cluster/infrastructure/ui-services/seasonUiService";
import GridSquadPlayers from "~/app/components/pages/teams-team/GridSquadPlayers.vue";

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/teams/[team]-[teamName]",
    es: "/equipos/[team]-[teamName]",
    it: "/squadre/[team]-[teamName]",
    pt: "/times/[team]-[teamName]",
    fr: "/equipes/[team]-[teamName]",
    de: "/mannschaften/[team]-[teamName]",
    ar: "/teams/[team]-[teamName]",
  },
});

definePageMeta({
  middleware: ["not-found-king-of-kings-nations"],
  layout: "team-and-player",
});

const route = useRoute();
const router = useRouter();
const localePath = useLocalePath();
const { t } = useI18n();
const { webContext, contextDomain, contextSiteTitle, webContextGenders } = useWebContext();
const { currentSeason, region } = useCompetitionStore();
const teamAndPlayerStore = useTeamAndPlayerStore();
const {
  getTeam,
  getCurrentTeamStaff,
  getSeasonTeamPlayers,
  getSeasonTeamStaff,
  getLastTurnPlayedByTeamInSeason,
  findTeamCurrentOrRecentSeason,
  getTeamGroupId,
  getTeamStanding,
  getSeasonTeamRankings,
  useFetchParamsTeamSeasons,
} = useTeams();
const { isSpain, isAmericas } = useRegions();
const { findRegionInfoByCompetitionId } = RegionUiService();

// Util function to initialize all data
const getSeasonTeamDataGlobal = async () => {
  showWorldCupStats.value = $SEASONS_WORLD_CUP.includes(seasonId.value);
  showNationsGroupsStats.value = $SEASONS_NATIONS_GROUPS.includes(seasonId.value);
  await getTeamData();
  await getSquadSeasonData();
  await getHistoricSeasonData();
};

//Data used from the start & general utils const
const teamId: number = Number(route.params.team);

const team = ref<Team>();
const currentTeamStaff = ref<Staff[]>([]);
const teamManager = ref<UnwrapRefSimple<Staff> | Staff | undefined>();
const teamSeasons = ref<Season[]>();

const loadingSeasonData = ref<boolean>(false);
const errorMessage = ref<boolean>(false);
const showWorldCupStats = ref<boolean>(false);
const showNationsGroupsStats = ref<boolean>(false);

//set value for teamSeasons const checking teamAndPlayerStore to avoid api calls
const setTeamSeasons = async (): Promise<void> => {
  if (!!teamAndPlayerStore.teamSeasonsPlayed) {
    teamSeasons.value = teamAndPlayerStore.teamSeasonsPlayed;
  } else {
    const { data, error } = await useFetch(useFetchParamsTeamSeasons(teamId));
    if (data.value) {
      teamSeasons.value = data.value;
    }
    if (error.value && error.value.statusCode === 404) {
      throw createError({ statusCode: 404, statusMessage: "Team not found" });
    }
  }
};
await setTeamSeasons();

//Initialize useSeasonFilter with initial values
const currentOrRecentTeamSeason = teamSeasons.value
  ? findTeamCurrentOrRecentSeason(teamSeasons.value)
  : undefined;
const initialValues = currentOrRecentTeamSeason
  ? {
      seasonClusterId: currentOrRecentTeamSeason.seasonClusterId,
      seasonId: currentOrRecentTeamSeason.id,
      regionId: findRegionInfoByCompetitionId(currentOrRecentTeamSeason.competitionId, "id"),
    }
  : undefined;
const { seasonId, seasonClusterId, regionId } = useSeasonFilter(initialValues);

//team basic data
const teamAdStoreImage = ref<string>();
const teamAdStoreLink = ref<string>();

const teamSocialProfiles = reactive<{ [key in SocialMediaNames]?: string | undefined }>({
  twitter: undefined,
  instagram: undefined,
  tiktok: undefined,
  twitch: undefined,
  youtube: undefined,
  facebook: undefined,
  kick: undefined,
});

async function getTeamData(): Promise<void> {
  try {
    team.value = await getTeam(teamId);

    const teamIdVariable = ref<number>();
    const seasonIdValue = seasonId.value;

    if (seasonIdValue === 8) {
      teamIdVariable.value = Number(team.value?.metaInformation?.kingdom_cup_team_id);
    } else {
      teamIdVariable.value = teamId;
    }

    const _teamIdVariable = teamIdVariable.value;
    currentTeamStaff.value = (await getSeasonTeamStaff(seasonIdValue, _teamIdVariable)) || [];

    teamManager.value =
      currentTeamStaff.value && seasonIdValue === 8
        ? currentTeamStaff.value.find(s => s.role === "president" && webContextGenders.includes(s.gender))
        : currentTeamStaff.value
          ? currentTeamStaff.value.find(s => s.role === "president")
          : undefined;

    teamAdStoreImage.value = team.value?.metaInformation?.store_image_url;
    teamAdStoreLink.value = team.value?.metaInformation?.store_ad_link;

    teamSocialProfiles.twitter = team.value?.metaInformation?.twitter_url;
    teamSocialProfiles.instagram = team.value?.metaInformation?.instagram_url;
    teamSocialProfiles.tiktok = team.value?.metaInformation?.tiktok_url;
    teamSocialProfiles.twitch = team.value?.metaInformation?.twitch_url;
    teamSocialProfiles.youtube = team.value?.metaInformation?.youtube_url;
    teamSocialProfiles.facebook = team.value?.metaInformation?.facebook_url;
    teamSocialProfiles.kick = team.value?.metaInformation?.kick_url;
  } catch (e) {
    console.error(e);
  }
}

//squad data
const gridPlayersKeys = ref(0);
const fetchedTeamPlayers = ref<Player[]>([]);
const seasonTeamStaff = ref<Staff[]>([]);
//const currentCoaches = ref<Staff[]>([]);
const teamCoaches = ref<Staff[]>([]);
const seasonMvpPartner = ref();

async function getSquadSeasonData(): Promise<void> {
  try {
    loadingSeasonData.value = true;
    errorMessage.value = false;

    const teamIdVariable = ref<number>();
    const seasonIdValue = seasonId.value;

    if (seasonIdValue === 8) {
      teamIdVariable.value = Number(team.value?.metaInformation?.kingdom_cup_team_id);
    } else {
      teamIdVariable.value = teamId;
    }

    const _teamIdVariable = teamIdVariable.value;
    if (seasonIdValue && _teamIdVariable) {
      fetchedTeamPlayers.value = (await getSeasonTeamPlayers(seasonIdValue, _teamIdVariable)) || [];
      seasonTeamStaff.value = (await getSeasonTeamStaff(seasonIdValue, _teamIdVariable)) || [];
      seasonMvpPartner.value =
        currentSeason && currentSeason.id === seasonIdValue
          ? currentSeason?.metaInformation?.stats_partner
          : undefined;
    }

    const _seasonTeamStaff = seasonTeamStaff.value;
    //const _currentTeamStaff = currentTeamStaff.value;
    // if (seasonIdValue === currentSeason?.id) {
    //   teamCoaches.value = _currentTeamStaff ? _currentTeamStaff.filter(s => s.role === "coach") : [];
    // } else {
    teamCoaches.value = _seasonTeamStaff ? _seasonTeamStaff.filter(s => s.role === "coach") : [];
  } catch (e) {
    errorMessage.value = true;
    console.error(e);
  } finally {
    if (!fetchedTeamPlayers.value?.length) {
      errorMessage.value = true;
      loadingSeasonData.value = false;
      gridPlayersKeys.value++;
    } else {
      loadingSeasonData.value = false;
      gridPlayersKeys.value++;
    }
  }
}

//historic data
const teamGroupId = ref<number>();
const teamGroupStanding = ref<Standing | undefined>();
const teamSeasonRankings = ref<TeamRankingParameter[]>([]);
const teamSeasonFinalMatch = ref<string | null>(null);
const gridHistoricDataKey = ref<number>(0);
const errorMessageHistory = ref<boolean>(false);

async function getHistoricSeasonData(): Promise<void> {
  try {
    loadingSeasonData.value = true;
    errorMessageHistory.value = false;

    const teamIdVariable = ref<number>();

    const seasonIdValue = seasonId.value;
    if (seasonIdValue === 8) {
      teamIdVariable.value = Number(team.value?.metaInformation?.kingdom_cup_team_id);
    } else {
      teamIdVariable.value = teamId;
    }

    const _teamIdVariable = teamIdVariable.value;

    if (seasonIdValue && _teamIdVariable) {
      teamGroupId.value = await getTeamGroupId(seasonIdValue, _teamIdVariable);
      const _teamGroupId = teamGroupId.value;

      teamGroupStanding.value = _teamGroupId
        ? await getTeamStanding(_teamGroupId, _teamIdVariable)
        : undefined;
      teamSeasonRankings.value = (await getSeasonTeamRankings(seasonIdValue, _teamIdVariable)) || [];
      teamSeasonFinalMatch.value = await getLastTurnPlayedByTeamInSeason(seasonIdValue, _teamIdVariable);
    }
  } catch (e) {
    errorMessageHistory.value = true;
    console.error(e);
  } finally {
    if (!teamGroupStanding.value && !teamSeasonRankings.value && !showWorldCupStats.value) {
      errorMessageHistory.value = true;
      loadingSeasonData.value = false;
      gridHistoricDataKey.value++;
    } else {
      loadingSeasonData.value = false;
      gridHistoricDataKey.value++;
    }
  }
}

//call global function to get all data
await getSeasonTeamDataGlobal();

// Utils functions
const teamMembersSchema = computed(() => {
  return teamCoaches.value.map(
    member => `{"@type": "Person", "role": "${member.role}", "name" : "${member.shortName}"}`
  );
});

const teamPlayersSchema = computed(() => {
  return fetchedTeamPlayers.value.map(player => {
    const capitalizedGender = player.gender
      ? player.gender.charAt(0).toUpperCase() + player.gender.slice(1)
      : "";

    const url = localePath({
      name: "players-playerId-playerName",
      params: {
        playerId: player.id,
        playerName: slugify(player.shortName),
      },
    });
    return `{"@type": "Person", "name" : "${player.shortName}", "jobTitle": "${player.role}", "gender": "https://schema.org/${capitalizedGender}", "identifier": "${player.jersey}", "url":"https://${contextDomain + url}"}`;
  });
});

const socialSchema = Object.values(teamSocialProfiles)
  .filter(link => link !== undefined)
  .map(link => `"${link}"`);

useHead({
  script: () => [
    {
      type: "application/ld+json",
      innerHTML: `{"@context": "https://schema.org", "@type": "SportsTeam", "name": "${team.value?.completeName}", "memberOf":"${contextSiteTitle}", "logo": "${team.value?.logo?.url}", "sameAs":[${socialSchema}],  "founder" : "${teamManager.value?.shortName}","coach":[${teamMembersSchema.value}], "athlete":[${teamPlayersSchema.value}]}`,
    },
  ],
});

const teamName = team.value?.completeName;
const metaTitle = t("meta.title.team", { teamName });
const metaDescription = t("meta.description.team", { teamName, contextSiteTitle });

useSeoMeta({
  title: () => metaTitle,
  ogTitle: () => metaTitle,
  description: () => metaDescription,
  ogDescription: () => metaDescription,
  ogImage: () => team.value?.logo.url,
});
</script>

<template>
  <section
    id="hero"
    class="relative flex justify-center items-center w-full h-[480px] md:h-[542px] overflow-hidden"
    v-if="team"
  >
    <hero-team-page
      :team="team"
      :team-social-profiles="teamSocialProfiles"
      :team-ad-store-image="teamAdStoreImage"
      :team-ad-store-link="teamAdStoreLink"
      :featuring-world-cup="$SEASONS_WORLD_CUP.includes(currentOrRecentTeamSeason?.id || 0)"
    ></hero-team-page>
  </section>

  <section
    v-if="team"
    id="teamSeasonInfo"
    class="p-4 md:p-9"
    :style="`background-image: linear-gradient(100deg, ${team.secondColorHEX} 0%, ${team.firstColorHEX} 100%)`"
  >
    <current-season-table-data-team-page
      v-if="currentOrRecentTeamSeason"
      :current-season="currentOrRecentTeamSeason"
      :team-id="teamId"
      :current-season-cluster="currentOrRecentTeamSeason.seasonClusterId"
    ></current-season-table-data-team-page>
  </section>

  <section class="py-8" id="teamSeasonMatches">
    <current-season-slider-matches-team
      v-if="currentOrRecentTeamSeason"
      :team-id="teamId"
      :team-seasons="teamSeasons"
      :region-reference="findRegionInfoByCompetitionId(currentOrRecentTeamSeason.competitionId, 'id')"
      with-add-to-calendar-in-section
    ></current-season-slider-matches-team>
  </section>

  <section class="py-8" id="teamSeasonSquad">
    <div class="px-4 md:px-6">
      <h2 class="kql-page-section-title">{{ $t("team.squad.title") }}</h2>
      <hr />
    </div>

    <div class="kql-w-container">
      <div class="text-right px-4 md:px-6">
        <team-seasons-filter
          v-model:season-cluster-id="seasonClusterId"
          v-model:season-id="seasonId"
          v-model:region-id="regionId"
          :team-id="teamId"
          :team-country-id="team?.countryId"
          :team-seasons="teamSeasons"
          :initial-filter-values="initialValues"
          @change="getSeasonTeamDataGlobal"
        ></team-seasons-filter>
      </div>

      <div class="grid grid-cols-1 xl:grid-cols-[336px_1fr_1fr] gap-4 px-0 bigsm:px-4 lg:px-6">
        <div class="flex gap-4 xl:block xl:max-h-none max-h-[513px] justify-center">
          <div v-if="teamManager && teamManager.image">
            <card-president-team-page
              :president-title="$t('teams.president.word')"
              :president-image="teamManager.image?.url || ''"
              :president-name="teamManager.shortName || ''"
            ></card-president-team-page>
          </div>
          <div class="min-w-[336px] hidden xl:block xl:mt-4 xl:w-auto xl:h-auto" v-if="team">
            <card-store-ad
              light
              :product-title="
                $t('card.store.productTitle.shirtTeamAmericas', { teamName: team.completeName })
              "
              :title-card="$t('card.store.officialStore.title')"
              :product-image="teamAdStoreImage"
              :product-link="teamAdStoreLink"
            ></card-store-ad>
          </div>
        </div>
        <div
          class="xl:col-start-2 xl:col-span-2 grid grid-cols-1 gap-2 bigsm:grid-cols-2 md:gap-4 relative"
          :key="gridPlayersKeys"
        >
          <base-backdrop-loading :active="loadingSeasonData"></base-backdrop-loading>

          <card-coach-team-page
            v-for="coach in teamCoaches"
            :staff-complete-name="coach.shortName"
            :staff-role="coach.role"
            :team-first-color-h-e-x="team?.firstColorHEX"
            :team-second-color-h-e-x="team?.secondColorHEX"
            :staff-image="coach?.image?.url"
          ></card-coach-team-page>

          <card-no-data-message
            v-if="errorMessage"
            :message="$t('shared.errorMessage.noData')"
          ></card-no-data-message>

          <grid-squad-players
            :players="fetchedTeamPlayers || []"
            :team="team"
            :season-id="seasonId"
            :region-id="regionId"
            :season-mvp-partner="seasonMvpPartner"
          ></grid-squad-players>
        </div>
      </div>
    </div>
  </section>

  <section v-if="seasonId !== 40" id="teamHistoryData" class="py-6 md:py-10 px-4 md:px-6">
    <div>
      <h2 class="kql-page-section-title">{{ $t("team.historic.title") }}</h2>
      <hr />
    </div>

    <div class="kql-w-container">
      <div class="text-right">
        <team-seasons-filter
          v-model:season-cluster-id="seasonClusterId"
          v-model:season-id="seasonId"
          v-model:region-id="regionId"
          :team-id="teamId"
          :team-country-id="team?.countryId"
          :team-seasons="teamSeasons"
          :initial-filter-values="initialValues"
          @change="getSeasonTeamDataGlobal"
        ></team-seasons-filter>
      </div>

      <div class="relative min-h-40">
        <base-backdrop-loading :active="loadingSeasonData"></base-backdrop-loading>
        <card-no-data-message
          v-if="errorMessageHistory"
          :message="$t('shared.errorMessage.noData')"
        ></card-no-data-message>

        <grid-historic-data-team-page
          v-if="!errorMessageHistory"
          :key="gridHistoricDataKey"
          :final-match="teamSeasonFinalMatch"
          :team-standing="teamGroupStanding"
          :team-rankings="teamSeasonRankings"
          :show-world-cup-stats="showWorldCupStats"
          :nations-group-stats="showNationsGroupsStats"
        ></grid-historic-data-team-page>
      </div>
    </div>
  </section>
</template>

<style scoped></style>
